import { APIPaths, StoreApi } from "@unit/apis";
import { useAtom } from "jotai";
import { useCallback, useMemo } from "react";

import { useAppSnackbar } from "@/custom-hooks/use-app-snackbar";
import { API_URL } from "@/global-state/firebase-settings";
import { favoriteStoresAtom, idTokenAtom, loadingAtom, storesAtom } from "@/global-state/jotai-atom";

export const useStoreApi = () => {
  const { setAppSnackbar } = useAppSnackbar();
  const [, setLoading] = useAtom(loadingAtom);
  const [idToken] = useAtom(idTokenAtom);
  const [, setStoreList] = useAtom(storesAtom);
  const [, favoriteStoreList] = useAtom(favoriteStoresAtom);
  const storeApi = useMemo(() => new StoreApi(API_URL, idToken), [idToken]);

  const fetchStoreList = useCallback(
    async (parameters: APIPaths["/app/store/list"]["get"]["parameters"]["query"]) => {
      await setLoading(true);
      try {
        const result = await storeApi.getStoreList(parameters);
        setStoreList(result.objects.filter((data) => ["open"].includes(data.status)));
        return result;
      } catch (e: any) {
        setAppSnackbar(e?.response?.data?.message || e?.response?.data?.devMessage || "error", {
          error: true,
        });
      } finally {
        await setLoading(false);
      }
    },
    [storeApi],
  );

  const fetchFavoriteStoreList = useCallback(
    async (parameters: APIPaths["/app/store/favorite/list"]["get"]["parameters"]["query"]) => {
      await setLoading(true);
      try {
        const result = await storeApi.getStoreFavoriteList(parameters);
        favoriteStoreList(result.objects.filter((data) => ["open"].includes(data.status)));
        return result;
      } catch (e: any) {
        setAppSnackbar(e?.response?.data?.message || e?.response?.data?.devMessage || "error", {
          error: true,
        });
      } finally {
        await setLoading(false);
      }
    },
    [storeApi],
  );

  const fetchStoreById = useCallback(
    async (id: APIPaths["/app/store/{id}"]["get"]["parameters"]["path"]["id"]) => {
      await setLoading(true);
      try {
        return await storeApi.getStoreById(id);
      } catch (e: any) {
        setAppSnackbar(e?.response?.data?.message || e?.response?.data?.devMessage || "error", {
          error: true,
        });
      } finally {
        await setLoading(false);
      }
    },
    [storeApi],
  );

  const addFavoriteStore = useCallback(
    async (id: APIPaths["/app/store/{id}/favorite"]["put"]["parameters"]["path"]["id"]) => {
      await setLoading(true);
      try {
        return await storeApi.putStoreFavoriteById(id);
      } finally {
        await setLoading(false);
      }
    },
    [storeApi],
  );

  const deleteFavoriteStore = useCallback(
    async (id: APIPaths["/app/store/{id}/favorite"]["delete"]["parameters"]["path"]["id"]) => {
      await setLoading(true);
      try {
        return await storeApi.deleteStoreFavoriteById(id);
      } finally {
        await setLoading(false);
      }
    },
    [storeApi],
  );

  return { fetchStoreList, fetchFavoriteStoreList, fetchStoreById, addFavoriteStore, deleteFavoriteStore };
};
